import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import type { CalendarEvent } from '@container-system/shared';
import { calendarApi } from '../../api/calendar';
import { useSocketEvent } from '../../app/socket';
import { Button } from '../../components/ui/Button';
import { Card, CardBody } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Input, Textarea } from '../../components/ui/Input';
import { PageHeader } from '../../components/ui/PageHeader';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';
import { formatDateOnly } from '../../utils/format';

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function toMonthKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
}

function toDateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Montag-basierte Wochen für die Monatsansicht - inkl. der Vor-/Nachlauf-Tage der Nachbarmonate, damit die Gitterstruktur immer vollständige Wochen bildet. */
function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // 0=Montag
  const gridStart = new Date(year, month, 1 - startOffset);
  return Array.from({ length: 42 }, (_, i) => new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
}

const WEEKDAY_HEADERS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function CalendarPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [editing, setEditing] = useState<CalendarEvent | 'new' | null>(null);

  const monthKey = toMonthKey(cursor);
  const eventsQuery = useQuery({ queryKey: ['calendar', monthKey], queryFn: () => calendarApi.listForMonth(monthKey) });

  useSocketEvent<{ id: string; title: string; date: string }>('reminder:due', (payload) => {
    toast.show(`Erinnerung: ${payload.title}`, 'info');
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
  });

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const ev of eventsQuery.data ?? []) {
      const list = map.get(ev.date) ?? [];
      list.push(ev);
      map.set(ev.date, list);
    }
    return map;
  }, [eventsQuery.data]);

  const days = buildMonthGrid(cursor.getFullYear(), cursor.getMonth());
  const todayKey = toDateKey(new Date());

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Kalender"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </Button>
            <span className="min-w-32 text-center text-sm font-medium text-ivory-200">
              {cursor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            </span>
            <Button variant="secondary" size="sm" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </Button>
          </div>
        }
      />

      <Card>
        <CardBody>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium uppercase text-ivory-500">
            {WEEKDAY_HEADERS.map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1">
            {days.map((day) => {
              const key = toDateKey(day);
              const inMonth = day.getMonth() === cursor.getMonth();
              const dayEvents = eventsByDate.get(key) ?? [];
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDate(key)}
                  className={`flex min-h-20 flex-col items-start gap-0.5 rounded border p-1.5 text-left text-xs transition-colors ${
                    inMonth ? 'border-surface-700 bg-surface-900' : 'border-surface-800 bg-surface-900/30 text-ivory-500'
                  } ${key === todayKey ? 'ring-1 ring-accent-500' : ''} hover:border-accent-500`}
                >
                  <span className={inMonth ? 'text-ivory-200' : 'text-ivory-600'}>{day.getDate()}</span>
                  {dayEvents.slice(0, 2).map((ev) => (
                    <span
                      key={ev.id}
                      className={`w-full truncate rounded px-1 py-0.5 ${ev.isReminder ? 'bg-accent-500/20 text-accent-400' : 'bg-surface-700 text-ivory-300'}`}
                    >
                      {ev.title}
                    </span>
                  ))}
                  {dayEvents.length > 2 && <span className="text-ivory-500">+{dayEvents.length - 2} mehr</span>}
                </button>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {selectedDate && (
        <DayModal
          dateKey={selectedDate}
          events={eventsByDate.get(selectedDate) ?? []}
          onClose={() => setSelectedDate(null)}
          onEdit={(ev) => setEditing(ev)}
          onNew={() => setEditing('new')}
        />
      )}

      {editing && (
        <EventFormModal initial={editing === 'new' ? null : editing} defaultDate={selectedDate ?? todayKey} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function DayModal({
  dateKey,
  events,
  onClose,
  onEdit,
  onNew,
}: {
  dateKey: string;
  events: CalendarEvent[];
  onClose: () => void;
  onEdit: (ev: CalendarEvent) => void;
  onNew: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (id: string) => calendarApi.remove(id),
    onSuccess: () => {
      toast.show('Termin gelöscht.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Löschen fehlgeschlagen.', 'error'),
  });

  return (
    <Modal open onClose={onClose} title={formatDateOnly(dateKey, { weekday: 'long', day: '2-digit', month: 'long' })}>
      <div className="flex flex-col gap-3">
        {events.length === 0 && <p className="text-sm text-ivory-500">Keine Termine an diesem Tag.</p>}
        {events.map((ev) => (
          <div key={ev.id} className="rounded-md border border-surface-600 bg-surface-800 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-1.5 text-sm font-medium text-ivory-100">
                  {ev.title}
                  {ev.isReminder && (
                    <span className="inline-flex items-center gap-1 text-accent-400">
                      <AlarmClock className="h-3.5 w-3.5" strokeWidth={1.75} /> {ev.reminderTime}
                    </span>
                  )}
                </p>
                {ev.description && <p className="mt-1 text-xs text-ivory-500">{ev.description}</p>}
              </div>
              <div className="flex shrink-0 gap-2 text-xs">
                <button className="font-medium text-accent-400 hover:underline" onClick={() => onEdit(ev)}>
                  Bearbeiten
                </button>
                <button className="flex items-center gap-1 font-medium text-danger-400 hover:underline" onClick={() => deleteMutation.mutate(ev.id)}>
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Löschen
                </button>
              </div>
            </div>
          </div>
        ))}
        <Button size="sm" onClick={onNew} className="mt-2 self-start">
          <Plus className="h-4 w-4" strokeWidth={2} /> Termin hinzufügen
        </Button>
      </div>
    </Modal>
  );
}

function EventFormModal({ initial, defaultDate, onClose }: { initial: CalendarEvent | null; defaultDate: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [date] = useState(initial?.date ?? defaultDate);
  const [isReminder, setIsReminder] = useState(initial?.isReminder ?? false);
  const [reminderTime, setReminderTime] = useState(initial?.reminderTime ?? '08:00');

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        date,
        title,
        description: description || null,
        isReminder,
        reminderTime: isReminder ? reminderTime : null,
      };
      return initial ? calendarApi.update(initial.id, payload) : calendarApi.create(payload);
    },
    onSuccess: () => {
      toast.show('Termin gespeichert.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['calendar'] });
      onClose();
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.', 'error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? 'Termin bearbeiten' : 'Neuer Termin'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!title}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Titel" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <Textarea label="Beschreibung" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />

        <label className="flex items-center gap-2 text-sm text-ivory-300">
          <input type="checkbox" checked={isReminder} onChange={(e) => setIsReminder(e.target.checked)} /> Erinnerung
        </label>
        {isReminder && <Input label="Uhrzeit" type="time" value={reminderTime} onChange={(e) => setReminderTime(e.target.value)} />}
      </div>
    </Modal>
  );
}
